import { FC, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classes from './tutorFinance.module.scss';

export const FinanceInfo: FC<{label: string; description: string}> = ({label, description}) => {
    const id = useId();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState({top: 0, left: 0});

    const cancelClose = () => {
        if(closeTimer.current) clearTimeout(closeTimer.current);
    };
    const show = () => {
        cancelClose();
        setOpen(true);
    };
    const closeAfterHover = () => {
        cancelClose();
        closeTimer.current = setTimeout(() => {
            if(document.activeElement !== buttonRef.current) setOpen(false);
        }, 150);
    };
    useEffect(() => () => cancelClose(), []);

    useEffect(() => {
        if(!open) return;

        const updatePosition = () => {
            const rect = buttonRef.current?.getBoundingClientRect();
            if(!rect) return;
            const width = Math.min(320, window.innerWidth - 24);
            const height = popupRef.current?.offsetHeight ?? 120;
            setPosition({
                left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)),
                top: rect.bottom + height + 8 <= window.innerHeight
                    ? rect.bottom + 8
                    : Math.max(12, rect.top - height - 8),
            });
        };
        const closeOutside = (event: PointerEvent) => {
            if(!buttonRef.current?.contains(event.target as Node)
                && !popupRef.current?.contains(event.target as Node)) setOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if(event.key === 'Escape') setOpen(false);
        };
        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        document.addEventListener('pointerdown', closeOutside);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
            document.removeEventListener('pointerdown', closeOutside);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    return <>
        <button
            ref={buttonRef}
            type="button"
            className={classes.infoButton}
            aria-label={`Как считается: ${label}`}
            aria-describedby={open ? id : undefined}
            aria-expanded={open}
            onMouseEnter={show}
            onMouseLeave={closeAfterHover}
            onFocus={show}
            onBlur={() => setOpen(false)}
            onClick={show}
        >i</button>
        {open && createPortal(
            <div ref={popupRef} id={id} role="tooltip" className={classes.tooltip} style={position}
                onMouseEnter={cancelClose} onMouseLeave={closeAfterHover}>
                {description}
            </div>,
            document.body,
        )}
    </>;
};
